import { Device, ICountryCode, IFrontPageConfig, ILocaleConfig, IPageProps } from '@interfaces';
import { ThemeProvider as MuiThemeProvider, THEME_ID } from '@mui/material/styles';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import App, { AppContext } from 'next/app';
import * as Sentry from '@sentry/nextjs';
import Router from 'next/router';
import React, { ReactElement } from 'react';
import { ThemeProvider, CacheProvider, EmotionCache } from '@emotion/react';
import { fetchBaseData } from '../src/api/resources';
import Footer from '../src/components/sections/footer/footer';
import Header from '../src/components/sections/header/Header';
import { DeviceProvider } from '../src/contexts/device/device';
import { AbTestsProvider, initABTests } from '../src/contexts/abTests/abTest';
import { LocaleConfigProvider } from '../src/contexts/locale-config/locale-config';
import { MUITheme, theme } from '../src/style/global-style';
import { getTheme } from '../src/theme';
import { DeviceDetection, getDetectedDevice } from '../src/utils/device-detection';
import { sendPageLoadEvent } from '../src/utils/EventTracker';
import {
    getDateAdapterLocale,
    setDefaultSortingString,
    setGtmId,
    setHtmlLang,
    setPortal,
    setSsrGtmId,
} from '../src/utils/locale';
import { TranslationService } from '../src/utils/translation-service';
import createEmotionCache from '../utils/createEmotionCache';
import type { AppProps } from 'next/app';
import { IabTests } from 'src/contexts/abTests/activeTests';
import { IsomorphicHelpers } from 'src/helpers/IsomorphicHelpers';

const SKIP_DEFAULT_LAYOUT_PAGES = ['/booking/[[...step]]'];

// Client-side cache, shared for the whole session of the user in the browser.
const clientSideEmotionCache = createEmotionCache();

export interface IAppProps extends AppProps<IPageProps> {
    detectedDevice: Device;
    data: {
        config?: IFrontPageConfig;
        error?: Error;
    };
    Component: any;
    hostUrl: string;
    pathname?: string;
    emotionCache?: EmotionCache;
    abTests: IabTests;
}

class MyApp extends App<IAppProps> {
    static async getInitialProps(appContext: AppContext): Promise<any> {
        const host = appContext?.ctx?.req?.headers?.host || window?.location?.host;
        const pathname = appContext.router.pathname;
        //@ts-ignore
        IsomorphicHelpers.setServerSide(appContext?.ctx?.req, appContext?.ctx?.res);
        const abTests = initABTests();

        const deviceHandle = (
            appContext.ctx.req ? appContext.ctx.req.headers['user-agent'] : navigator.userAgent
        ) as string;

        setPortal(host);

        DeviceDetection.detectDevice(deviceHandle, true); // SSR Detection

        const initialProps = {
            pathname,
            hostUrl: `https://${host}`,
            data: {},
        };

        let data = {} as IFrontPageConfig;

        try {
            data = await fetchBaseData();
        } catch (error) {
            if (error instanceof Error) {
                initialProps.data = { error: error.message };
            } else {
                initialProps.data = { error: String(error) };
            }
            return initialProps;
        }

        const localeConfigData = data.localeConfig;

        setGtmId(localeConfigData.gtmId);
        setSsrGtmId(localeConfigData.ssrGtmId);
        setHtmlLang(localeConfigData.locale);
        setDefaultSortingString(localeConfigData.defaultSortingString);

        const appProps = await App.getInitialProps(appContext);

        return {
            ...appProps,
            ...initialProps,
            abTests: { ...abTests },
            data: {
                config: data,
            },
        };
    }
    constructor(props: any, context: any) {
        super(props, context);
        if (typeof window !== 'undefined') {
            setPortal(window.location.hostname);
        }
    }
    public componentDidMount() {
        // Remove the server-side injected CSS.
        const jssStyles = document.querySelector('#jss-server-side');
        jssStyles?.parentNode?.removeChild(jssStyles);

        Router.events.on('routeChangeComplete', () => sendPageLoadEvent());

        const isUserAgentMismatched = DeviceDetection.currentUserAgent !== navigator.userAgent;

        // Log mismatch user agent on SSR and CSR
        if (DeviceDetection.isDetected && isUserAgentMismatched) {
            console.error('Mismatch user agent', {
                detectedDeviceSSR: DeviceDetection.detectedDevice,
                userAgentSSR: DeviceDetection.currentUserAgent,
                userAgentClient: navigator.userAgent,
            });
            Sentry.captureException('Mismatch user agent', (scope) => {
                scope.setExtra('extraData', {
                    detectedDeviceSSR: DeviceDetection.detectedDevice,
                    userAgentSSR: DeviceDetection.currentUserAgent,
                    userAgentClient: navigator.userAgent,
                });
                return scope;
            });
        }
    }

    public render() {
        if (!this.props || !this.props.data) {
            throw new Error('App called without data');
        }
        if (this.props.data.error !== undefined) {
            throw new Error(`😱 App data request received errors \n\n ${this.props.data.error}`);
        }
        if (!this.props.data.config) {
            throw new Error('App called without config');
        }

        const {
            Component,
            emotionCache = clientSideEmotionCache,
            pageProps,
            hostUrl,
            pathname = '',
            abTests,
        } = this.props;

        const {
            header,
            footer,
            headerMobile,
            headerContent,
            footerMobile,
            // infoBanner,
            // promotionBanner,
            localeConfig,
            // uspBar: uspBarData,
            // branding: brandingData,
            translations: t,
        } = this.props.data.config; // @TODO: Handle types to recognize loading=true

        TranslationService.update(t);

        // Device initializer for DeviceProvider
        if (!DeviceDetection.isDetected) {
            DeviceDetection.detectDevice(navigator.userAgent);
        }

        const device = getDetectedDevice();
        const adapterLocale = getDateAdapterLocale(localeConfig.locale);

        const commonTheme = { ...theme, ...getTheme(localeConfig.code) };

        const DefaultProviders = ({ children }: { children: ReactElement }) => (
            <CacheProvider value={emotionCache}>
                <AbTestsProvider value={abTests}>
                    <DeviceProvider device={device}>
                        <LocaleConfigProvider config={localeConfig}>
                            <LocalizationProvider
                                dateAdapter={AdapterDateFns}
                                adapterLocale={adapterLocale}
                            >
                                <ThemeProvider theme={commonTheme}>
                                    <MuiThemeProvider
                                        theme={{
                                            [THEME_ID]: MUITheme,
                                        }}
                                    >
                                        {children}
                                    </MuiThemeProvider>
                                </ThemeProvider>
                            </LocalizationProvider>
                        </LocaleConfigProvider>
                    </DeviceProvider>
                </AbTestsProvider>
            </CacheProvider>
        );

        if (SKIP_DEFAULT_LAYOUT_PAGES.includes(pathname)) {
            return (
                <DefaultProviders>
                    <Component
                        pageProps={pageProps}
                        headerData={{
                            header,
                            headerMobile,
                        }}
                        headerContent={headerContent}
                        footerData={{
                            footer,
                            footerMobile,
                        }}
                    />
                </DefaultProviders>
            );
        }

        return (
            <DefaultProviders>
                <>
                    <Header
                        desktopHeaderData={header}
                        mobileHeaderData={headerMobile}
                        headerContent={headerContent}
                    />
                    <Component pageProps={pageProps} hostUrl={hostUrl} />
                    <Footer footer={footer} footerMobile={footerMobile} pageProps={pageProps} />
                </>
            </DefaultProviders>
        );
    }
}

export function reportWebVitals(metric: any) {
    console.log(metric);
}

export default MyApp;
